/**
 * Request and mutation state, shared by every dashboard feature — Task 13.1.
 *
 * These were written for the Content Console and none of them is about content: a loading/ready/error
 * triple, a 403-versus-everything-else classification, and the field errors a form attaches to inputs are
 * the same three problems on any authenticated screen. Task 13 adds the support queue, which needs all
 * three, so they move here rather than being copied — a second copy of `accessFor` is a second place for
 * "which status means the role is not allowed" to drift.
 *
 * `content/hooks.ts` re-exports them, so its own imports and tests are unchanged.
 */
import type { FieldError } from '@apuestapr/contracts';
import { useCallback, useEffect, useState } from 'react';

import type { ApiFailure, ApiResult } from './client.ts';

export type RequestState<Value> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly data: Value }
  | {
      readonly status: 'error';
      readonly failure: ApiFailure;
      readonly access: 'forbidden' | 'failure';
    };

export type MutationState<Value> =
  | { readonly status: 'ready'; readonly data?: Value }
  | { readonly status: 'loading' }
  | {
      readonly status: 'error';
      readonly failure: ApiFailure;
      readonly access: 'forbidden' | 'failure';
    };

/**
 * A 403 is the server saying this role may not do this, which a screen renders differently from a fault:
 * one is a permanent answer about the session, the other is worth retrying.
 */
export const accessFor = (failure: ApiFailure): 'forbidden' | 'failure' =>
  failure.kind === 'http' && failure.status === 403 ? 'forbidden' : 'failure';

/** Field-addressable details, which the API sends for a rejected body (400) or a conflict (409). */
export const fieldErrorsFor = (failure: ApiFailure): readonly FieldError[] =>
  failure.kind === 'http' && (failure.status === 400 || failure.status === 409)
    ? (failure.details ?? [])
    : [];

export const useRequest = <Value>(
  load: () => Promise<ApiResult<Value>>,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): RequestState<Value> => {
  const [state, setState] = useState<RequestState<Value>>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    // Reset after the effect returns so React does not perform a cascading synchronous render.
    // The initial state is already loading; this reset covers a changed loader (route/id).
    queueMicrotask(() => {
      if (active) setState({ status: 'loading' });
    });
    void load().then((result) => {
      if (!active) return;
      if (result.ok) {
        setState({ status: 'ready', data: result.data });
        return;
      }
      onFailure?.(result.failure);
      setState({ status: 'error', failure: result.failure, access: accessFor(result.failure) });
    });
    return () => {
      active = false;
    };
  }, [load, onFailure]);

  return state;
};

/** Generic typed mutation state. The caller decides what a success does to the screen. */
export const useApiMutation = <Arguments extends readonly unknown[], Value>(
  mutate: (...arguments_: Arguments) => Promise<ApiResult<Value>>,
  onFailure?: ((failure: ApiFailure) => void) | undefined,
): {
  readonly state: MutationState<Value>;
  readonly execute: (...arguments_: Arguments) => Promise<ApiResult<Value>>;
  readonly reset: () => void;
} => {
  const [state, setState] = useState<MutationState<Value>>({ status: 'ready' });
  const execute = useCallback(
    async (...arguments_: Arguments): Promise<ApiResult<Value>> => {
      setState({ status: 'loading' });
      const result = await mutate(...arguments_);
      if (result.ok) setState({ status: 'ready', data: result.data });
      else {
        onFailure?.(result.failure);
        setState({ status: 'error', failure: result.failure, access: accessFor(result.failure) });
      }
      return result;
    },
    [mutate, onFailure],
  );
  const reset = useCallback(() => setState({ status: 'ready' }), []);
  return { state, execute, reset };
};
